import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import SuperAdminLayout from '../components/SuperAdminLayout';
import CollapsibleTable from '../components/CollapsibleTable';
import axios from 'axios';
import { Plus, Trash2, Edit, CheckCircle, XCircle, Clock, LogIn, Download, Upload, Calendar, Eye } from 'lucide-react';
import { useNavigate } from '../utils/SimpleRouter';
import { Switch } from '../components/ui/switch';
import toast from '../utils/toast';
import confirm from '../utils/confirm';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminOrganizations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    language: 'no', // Default to Norwegian
    billing_address_line1: '',
    billing_address_line2: '',
    billing_postal_code: '',
    billing_city: '',
    billing_country: 'Norge',
    billing_email: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    // Order form fields
    org_number: '',
    invoice_email: '',
    invoice_address: '',
    invoice_postal_code: '',
    invoice_city: '',
    contact_person: '',
    number_of_entrances: '',
    order_type: '',
    needs_screen: false,
    screen_orientation: '',
    additional_info: ''
  });

  useEffect(() => {
    fetchOrganizations();
    fetchAvailableLanguages();
  }, []);

  const fetchAvailableLanguages = async () => {
    try {
      const response = await axios.get(`${API}/languages`);
      setAvailableLanguages(response.data);
    } catch (error) {
      console.error('Failed to fetch languages:', error);
      // Set default fallback languages
      setAvailableLanguages([
        { code: 'no', name: 'Norsk (Bokmål)' },
        { code: 'en', name: 'English' }
      ]);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Fetched organizations successfully:', response.data?.length, 'orgs');
      if (response.data && Array.isArray(response.data)) {
        setOrganizations(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error.response?.status, error.message);
      // Only show error toast if we don't have any data yet
      if (organizations.length === 0) {
        toast.error(t('superAdmin.organizations.loadError', 'Kunne ikke laste organisasjoner'));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/organizations`, formData);
      toast.success('Organisasjon opprettet!');
      setShowModal(false);
      setFormData({ 
        name: '', 
        address: '',
        language: 'no',
        billing_address_line1: '',
        billing_address_line2: '',
        billing_postal_code: '',
        billing_city: '',
        billing_country: 'Norge',
        billing_email: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        org_number: '',
        invoice_email: '',
        invoice_address: '',
        invoice_postal_code: '',
        invoice_city: '',
        contact_person: '',
        number_of_entrances: '',
        order_type: '',
        needs_screen: false,
        screen_orientation: '',
        additional_info: ''
      });
      fetchOrganizations();
    } catch (error) {
      toast.error('Kunne ikke opprette organisasjon');
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setFormData({
      name: org.name || '',
      address: org.address || '',
      language: org.language || 'no',
      billing_address_line1: org.billing_address_line1 || '',
      billing_address_line2: org.billing_address_line2 || '',
      billing_postal_code: org.billing_postal_code || '',
      billing_city: org.billing_city || '',
      billing_country: org.billing_country || 'Norge',
      billing_email: org.billing_email || '',
      contact_name: org.contact_name || '',
      contact_email: org.contact_email || '',
      contact_phone: org.contact_phone || '',
      org_number: org.org_number || '',
      invoice_email: org.invoice_email || '',
      invoice_address: org.invoice_address || '',
      invoice_postal_code: org.invoice_postal_code || '',
      invoice_city: org.invoice_city || '',
      contact_person: org.contact_person || '',
      number_of_entrances: org.number_of_entrances || '',
      order_type: org.order_type || '',
      needs_screen: org.needs_screen || false,
      screen_orientation: org.screen_orientation || '',
      additional_info: org.additional_info || ''
    });
    setShowModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/organizations/${editingOrg.id}`, formData);
      toast.success('Organisasjon oppdatert!');
      setShowModal(false);
      setEditingOrg(null);
      setFormData({ 
        name: '', 
        address: '',
        language: 'no',
        billing_address_line1: '',
        billing_address_line2: '',
        billing_postal_code: '',
        billing_city: '',
        billing_country: 'Norge',
        billing_email: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        org_number: '',
        invoice_email: '',
        invoice_address: '',
        invoice_postal_code: '',
        invoice_city: '',
        contact_person: '',
        number_of_entrances: '',
        order_type: '',
        needs_screen: false,
        screen_orientation: '',
        additional_info: ''
      });
      fetchOrganizations();
    } catch (error) {
      toast.error('Kunne ikke oppdatere organisasjon');
    }
  };

  const handleDelete = async (orgId) => {
    if (!await confirm.danger('Er du sikker på at du vil slette denne organisasjonen?')) return;
    
    try {
      await axios.delete(`${API}/admin/organizations/${orgId}`);
      toast.success('Organisasjon slettet!');
      fetchOrganizations();
    } catch (error) {
      toast.error('Kunne ikke slette organisasjon');
    }
  };

  const toggleSubscriptionTier = async (orgId, currentTier) => {
    const newTier = currentTier === 'demo' ? 'paid' : 'demo';
    const confirmMsg = newTier === 'paid' 
      ? 'Konvertere til betalt kunde?' 
      : 'Konvertere til demo-kunde?';
    
    if (!await confirm.info(confirmMsg)) return;
    
    try {
      const endpoint = newTier === 'paid' 
        ? `${API}/admin/organizations/${orgId}/convert-to-paid`
        : `${API}/admin/organizations/${orgId}/convert-to-demo`;
      
      await axios.post(endpoint, {});
      
      toast.success(`Organisasjon endret til ${newTier === 'paid' ? 'betalt' : 'demo'} kunde!`);
      fetchOrganizations();
    } catch (error) {
      toast.error('Kunne ikke endre abonnementstype');
    }
  };

  const updateDemoExpiry = async (orgId, newDate) => {
    try {
      await axios.put(`${API}/admin/organizations/${orgId}/demo-expiry`, {
        demo_expires_at: newDate
      });
      toast.success('Demo-utløpsdato oppdatert!');
      fetchOrganizations();
    } catch (error) {
      console.error('Failed to update demo expiry:', error);
      toast.error('Kunne ikke oppdatere demo-utløpsdato');
    }
  };

  const handleImpersonate = async (orgId, orgName) => {
    try {
      const response = await axios.post(`${API}/admin/impersonate/${orgId}`, {});
      
      // Open in new window/tab instead of replacing current session
      const newWindow = window.open('about:blank', '_blank');
      
      // Write a small script to set token and redirect
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head><title>Laster...</title></head>
            <body>
              <p>Åpner kunde-panel for ${orgName}...</p>
              <script>
                // Use sessionStorage for impersonation to avoid affecting other windows
                sessionStorage.setItem('token', '${response.data.access_token}');
                sessionStorage.setItem('user', JSON.stringify({
                  impersonating: true,
                  organization_id: '${orgId}',
                  organization_name: '${orgName}'
                }));
                sessionStorage.setItem('isImpersonationWindow', 'true');
                window.location.href = '/dashboard';
              </script>
            </body>
          </html>
        `);
        newWindow.document.close();
        
        toast.success(`Åpner ${orgName} i nytt vindu`);
      } else {
        toast.error('Kunne ikke åpne nytt vindu. Sjekk popup-blocker.');
      }
    } catch (error) {
      console.error('Impersonation failed:', error);
      toast.error('Kunne ikke logge inn som kunde');
    }
  };

  const downloadBackup = async (orgId, orgName) => {
    try {
      const response = await axios.get(`${API}/admin/organizations/${orgId}/backup`);
      
      // Convert JSON response to formatted string
      const jsonStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${orgName.replace(/[^a-zA-Z0-9]/g, '_')}_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup lastet ned!');
    } catch (error) {
      console.error('Backup error:', error);
      toast.error('Kunne ikke laste ned backup');
    }
  };

  const handleRestoreBackup = (orgId, orgName) => {
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Confirm action
      if (!await confirm.warning(`Er du sikker på at du vil gjenopprette backup for "${orgName}"?\n\nADVARSEL: Dette vil overskrive ALL eksisterende data for denne organisasjonen!`, {
        title: 'Bekreft gjenoppretting'
      })) {
        return;
      }
      
      try {
        const formData = new FormData();
        formData.append('backup_file', file);
        
        const response = await axios.post(`${API}/admin/organizations/${orgId}/restore`, formData, {
          headers: { 
            'Content-Type': 'multipart/form-data'
          }
        });
        
        toast.success(`Backup gjenopprettet! ${response.data.restored.screens} skjermer, ${response.data.restored.messages} meldinger, ${response.data.restored.articles} artikler, ${response.data.restored.users} brukere.`);
        fetchOrganizations(); // Refresh list
      } catch (error) {
        console.error('Restore error:', error);
        toast.error(error.response?.data?.detail || 'Kunne ikke gjenopprette backup');
      }
    };
    input.click();
  };

  const toggleOpenDemo = async (orgId, currentStatus) => {
    try {
      const token = getToken();
      await axios.post(`${API}/admin/organizations/${orgId}/toggle-open-demo`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentStatus ? 'Organisasjon er ikke lenger åpen demo' : 'Organisasjon satt som åpen demo');
      fetchOrganizations();
    } catch (error) {
      console.error('Failed to toggle open demo:', error);
      toast.error('Kunne ikke endre åpen demo-status');
    }
  };

  return (
    <SuperAdminLayout>
      <div className="admin-header">
        <h1>{t('superAdmin.organizations.title')}</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={20} /> {t('superAdmin.organizations.newOrganization')}
        </button>
      </div>

      <CollapsibleTable
        columns={[
          {
            key: 'name',
            label: t('common.name'),
            minWidth: '150px',
            render: (org) => <strong>{org.name}</strong>
          },
          {
            key: 'address',
            label: t('superAdmin.organizations.billingAddress1'),
            minWidth: '150px',
            render: (org) => <span style={{ fontSize: '0.9rem', opacity: 0.8, color: 'white' }}>{org.address || '-'}</span>
          },
          {
            key: 'subscription_tier',
            label: t('superAdmin.organizations.subscription'),
            minWidth: '150px',
            render: (org) => {
              const isExpired = org.demo_expires_at && new Date(org.demo_expires_at) < new Date();
              return (
                <>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.75rem'
                  }}>
                    <Switch
                      checked={org.subscription_tier === 'paid'}
                      onCheckedChange={() => toggleSubscriptionTier(org.id, org.subscription_tier)}
                      style={{
                        backgroundColor: org.subscription_tier === 'paid' ? '#10b981' : '#f59e0b'
                      }}
                    />
                    <span style={{ 
                      fontWeight: '600',
                      color: org.subscription_tier === 'paid' ? '#10b981' : '#f59e0b'
                    }}>
                      {org.subscription_tier === 'paid' ? t('superAdmin.organizations.paid') : t('superAdmin.organizations.demo')}
                    </span>
                  </div>
                </>
              );
            }
          },
          {
            key: 'demo_expires_at',
            label: 'Demo utløper',
            minWidth: '140px',
            render: (org) => {
              if (org.subscription_tier === 'paid') {
                return <span style={{ opacity: 0.4, fontSize: '0.85rem' }}>-</span>;
              }
              const isExpired = org.demo_expires_at && new Date(org.demo_expires_at) < new Date();
              const currentDate = org.demo_expires_at ? org.demo_expires_at.split('T')[0] : '';
              
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="date"
                    value={currentDate}
                    onChange={(e) => {
                      if (e.target.value) {
                        updateDemoExpiry(org.id, e.target.value + 'T23:59:59.000Z');
                      }
                    }}
                    style={{
                      background: isExpired ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                      border: isExpired ? '1px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      padding: '0.25rem 0.5rem',
                      color: isExpired ? '#ef4444' : '#fff',
                      fontSize: '0.85rem',
                      width: '130px'
                    }}
                  />
                  {isExpired && (
                    <span style={{ 
                      color: '#ef4444', 
                      fontSize: '0.7rem',
                      fontWeight: '600'
                    }}>
                      UTLØPT
                    </span>
                  )}
                </div>
              );
            }
          },
          {
            key: 'is_open_demo',
            label: 'Åpen Demo',
            minWidth: '100px',
            render: (org) => (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem'
              }}>
                <Switch
                  checked={org.is_open_demo || false}
                  onCheckedChange={() => toggleOpenDemo(org.id, org.is_open_demo)}
                  style={{
                    backgroundColor: org.is_open_demo ? '#8b5cf6' : '#6b7280'
                  }}
                />
                {org.is_open_demo && (
                  <Eye size={16} style={{ color: '#8b5cf6' }} />
                )}
              </div>
            )
          },
          {
            key: 'screen_count',
            label: t('superAdmin.organizations.screens'),
            minWidth: '80px',
            render: (org) => org.screen_count || 0
          },
          {
            key: 'admin_count',
            label: t('superAdmin.organizations.users'),
            minWidth: '80px',
            render: (org) => org.admin_count || 0
          },
          {
            key: 'is_active',
            label: t('common.status'),
            minWidth: '100px',
            render: (org) => org.is_active ? (
              <span className="badge badge-success">
                <CheckCircle size={14} /> {t('common.active')}
              </span>
            ) : (
              <span className="badge badge-secondary">
                <XCircle size={14} /> {t('common.inactive')}
              </span>
            )
          }
        ]}
        data={organizations}
        actions={(org) => (
          <>
            <button
              className="btn btn-primary"
              style={{ fontSize: '0.8rem', padding: 'clamp(0.4rem, 1vw, 0.5rem) 0.75rem' }}
              onClick={() => handleImpersonate(org.id, org.name)}
              title={t('superAdmin.organizations.loginAsOrg')}
            >
              <LogIn size={window.innerWidth < 1280 ? 12 : 14} />
            </button>
            <button
              className="btn btn-info"
              style={{ fontSize: '0.8rem', padding: 'clamp(0.4rem, 1vw, 0.5rem) 0.75rem' }}
              onClick={() => downloadBackup(org.id, org.name)}
              title={t('superAdmin.organizations.downloadBackup')}
            >
              <Download size={window.innerWidth < 1280 ? 12 : 14} />
            </button>
            <button
              className="btn btn-success"
              style={{ fontSize: '0.8rem', padding: 'clamp(0.4rem, 1vw, 0.5rem) 0.75rem' }}
              onClick={() => handleRestoreBackup(org.id, org.name)}
              title={t('superAdmin.organizations.restoreBackup')}
            >
              <Upload size={window.innerWidth < 1280 ? 12 : 14} />
            </button>
            <button 
              className="btn btn-secondary" 
              style={{ fontSize: '0.8rem', padding: 'clamp(0.4rem, 1vw, 0.5rem) 0.75rem' }}
              onClick={() => handleEdit(org)}
              title={t('superAdmin.organizations.edit')}
            >
              <Edit size={window.innerWidth < 1280 ? 12 : 14} />
            </button>
            <button
              className="btn btn-danger"
              style={{ fontSize: '0.8rem', padding: 'clamp(0.4rem, 1vw, 0.5rem) 0.75rem' }}
              onClick={() => handleDelete(org.id)}
              data-testid={`delete-org-${org.id}`}
            >
              <Trash2 size={window.innerWidth < 1280 ? 12 : 14} />
            </button>
          </>
        )}
      />

      {/* Create Organization Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => {
          setShowModal(false);
          setEditingOrg(null);
          setFormData({ 
            name: '', 
            address: '',
            billing_address_line1: '',
            billing_address_line2: '',
            billing_postal_code: '',
            billing_city: '',
            billing_country: 'Norge',
            billing_email: '',
            contact_name: '',
            contact_email: '',
            contact_phone: '',
            org_number: '',
            invoice_email: '',
            invoice_address: '',
            invoice_postal_code: '',
            invoice_city: '',
            contact_person: '',
            number_of_entrances: '',
            order_type: '',
            needs_screen: false,
            screen_orientation: '',
            additional_info: ''
          });
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingOrg ? t('superAdmin.organizations.editOrganization') : t('superAdmin.organizations.newOrganization')}</h2>
            <form onSubmit={editingOrg ? handleUpdate : handleSubmit}>
              
              {/* Basic Information */}
              <h3 style={{ color: '#50E3C2', marginTop: '0', marginBottom: '1rem', fontSize: '1.1rem' }}>{t('superAdmin.organizations.basicInfo')}</h3>
              <div className="form-group">
                <label>{t('superAdmin.organizations.organizationName')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="org-name-input"
                />
              </div>
              <div className="form-group">
                <label>{t('superAdmin.organizations.visitingAddress')}</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  data-testid="org-address-input"
                />
              </div>

              {/* Order Form Fields */}
              <h3 style={{ color: '#50E3C2', marginTop: '1.5rem', marginBottom: '1rem', fontSize: '1.1rem' }}>{t('superAdmin.organizations.orderInfo')}</h3>
              <div className="form-group">
                <label>{t('superAdmin.organizations.orgNumber')}</label>
                <input
                  type="text"
                  value={formData.org_number}
                  onChange={(e) => setFormData({...formData, org_number: e.target.value})}
                  placeholder="123456789"
                />
              </div>
              <div className="form-group">
                <label>{t('superAdmin.organizations.numberOfEntrances')}</label>
                <input
                  type="number"
                  value={formData.number_of_entrances}
                  onChange={(e) => setFormData({...formData, number_of_entrances: e.target.value})}
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label>{t('superAdmin.organizations.orderType')}</label>
                <select
                  value={formData.order_type}
                  onChange={(e) => setFormData({...formData, order_type: e.target.value})}
                  style={{ color: 'white', backgroundColor: '#2A2A2A' }}
                >
                  <option value="">{t('superAdmin.organizations.selectType')}</option>
                  <option value="demo">{t('superAdmin.organizations.demo')}</option>
                  <option value="trial">{t('superAdmin.organizations.trial')}</option>
                  <option value="paid">{t('superAdmin.organizations.paid')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('superAdmin.organizations.defaultLanguage', 'Standard språk')}</label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData({...formData, language: e.target.value})}
                  style={{ color: 'white', backgroundColor: '#2A2A2A' }}
                >
                  <option value="no" style={{ color: 'black', backgroundColor: 'white' }}>Norsk (Bokmål)</option>
                  <option value="en" style={{ color: 'black', backgroundColor: 'white' }}>English</option>
                  <option value="sv" style={{ color: 'black', backgroundColor: 'white' }}>Svenska</option>
                  <option value="dk" style={{ color: 'black', backgroundColor: 'white' }}>Dansk</option>
                  <option value="pl" style={{ color: 'black', backgroundColor: 'white' }}>Polish</option>
                  <option value="de" style={{ color: 'black', backgroundColor: 'white' }}>Deutsch</option>
                </select>
                <small style={{ color: '#a0a0a0', marginTop: '0.5rem', display: 'block' }}>
                  {t('superAdmin.organizations.languageHelpText', 'Dette språket vil brukes som standard når kunden logger inn')}
                </small>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.needs_screen}
                    onChange={(e) => setFormData({...formData, needs_screen: e.target.checked})}
                    style={{ width: 'auto' }}
                  />
                  {t('superAdmin.organizations.needsScreen')}
                </label>
              </div>
              <div className="form-group">
                <label>{t('superAdmin.organizations.screenOrientation')}</label>
                <select
                  value={formData.screen_orientation}
                  onChange={(e) => setFormData({...formData, screen_orientation: e.target.value})}
                  style={{ color: 'white', backgroundColor: '#2A2A2A' }}
                >
                  <option value="">{t('superAdmin.organizations.selectOrientation')}</option>
                  <option value="landscape">{t('superAdmin.organizations.landscape')}</option>
                  <option value="portrait">{t('superAdmin.organizations.portrait')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('superAdmin.organizations.additionalInfo')}</label>
                <textarea
                  value={formData.additional_info}
                  onChange={(e) => setFormData({...formData, additional_info: e.target.value})}
                  placeholder={t('superAdmin.organizations.additionalInfo')}
                  rows="4"
                  style={{ color: 'white', backgroundColor: '#2A2A2A', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', padding: '0.5rem' }}
                ></textarea>
              </div>

              {/* Billing Information */}
              <h3 style={{ color: '#50E3C2', marginTop: '1.5rem', marginBottom: '1rem', fontSize: '1.1rem' }}>{t('superAdmin.organizations.billingInfo')}</h3>
              <div className="form-group">
                <label>{t('superAdmin.organizations.billingAddress1')}</label>
                <input
                  type="text"
                  value={formData.billing_address_line1}
                  onChange={(e) => setFormData({...formData, billing_address_line1: e.target.value})}
                  placeholder={t('superAdmin.organizations.billingAddress1')}
                />
              </div>
              <div className="form-group">
                <label>{t('superAdmin.organizations.billingAddress2')}</label>
                <input
                  type="text"
                  value={formData.billing_address_line2}
                  onChange={(e) => setFormData({...formData, billing_address_line2: e.target.value})}
                  placeholder={t('superAdmin.organizations.billingAddress2')}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>{t('superAdmin.organizations.billingPostalCode')}</label>
                  <input
                    type="text"
                    value={formData.billing_postal_code}
                    onChange={(e) => setFormData({...formData, billing_postal_code: e.target.value})}
                    placeholder="0000"
                  />
                </div>
                <div className="form-group">
                  <label>{t('superAdmin.organizations.billingCity')}</label>
                  <input
                    type="text"
                    value={formData.billing_city}
                    onChange={(e) => setFormData({...formData, billing_city: e.target.value})}
                    placeholder={t('superAdmin.organizations.billingCity')}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('superAdmin.organizations.billingCountry')}</label>
                <input
                  type="text"
                  value={formData.billing_country}
                  onChange={(e) => setFormData({...formData, billing_country: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>{t('superAdmin.organizations.billingEmail')}</label>
                <input
                  type="email"
                  value={formData.billing_email}
                  onChange={(e) => setFormData({...formData, billing_email: e.target.value})}
                  placeholder="faktura@bedrift.no"
                />
              </div>

              {/* Alternative Invoice Information (from order form) */}
              <h3 style={{ color: '#50E3C2', marginTop: '1.5rem', marginBottom: '1rem', fontSize: '1.1rem' }}>{t('superAdmin.organizations.billingInfo')}</h3>
              <div className="form-group">
                <label>{t('superAdmin.organizations.billingEmail')}</label>
                <input
                  type="email"
                  value={formData.invoice_email}
                  onChange={(e) => setFormData({...formData, invoice_email: e.target.value})}
                  placeholder="faktura@firma.no"
                />
              </div>
              <div className="form-group">
                <label>Fakturaadresse (bestilling)</label>
                <input
                  type="text"
                  value={formData.invoice_address}
                  onChange={(e) => setFormData({...formData, invoice_address: e.target.value})}
                  placeholder="Gateadresse"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>Postnummer (bestilling)</label>
                  <input
                    type="text"
                    value={formData.invoice_postal_code}
                    onChange={(e) => setFormData({...formData, invoice_postal_code: e.target.value})}
                    placeholder="0000"
                  />
                </div>
                <div className="form-group">
                  <label>Poststed (bestilling)</label>
                  <input
                    type="text"
                    value={formData.invoice_city}
                    onChange={(e) => setFormData({...formData, invoice_city: e.target.value})}
                    placeholder="By"
                  />
                </div>
              </div>

              {/* Contact Person */}
              <h3 style={{ color: '#50E3C2', marginTop: '1.5rem', marginBottom: '1rem', fontSize: '1.1rem' }}>Kontaktperson</h3>
              <div className="form-group">
                <label>Navn</label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  placeholder="Fornavn Etternavn"
                />
              </div>
              <div className="form-group">
                <label>Kontaktperson (fra bestilling)</label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  placeholder="Kontaktperson navn"
                />
              </div>
              <div className="form-group">
                <label>E-post</label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  placeholder="kontakt@bedrift.no"
                />
              </div>
              <div className="form-group">
                <label>Mobilnummer</label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  placeholder="+47 123 45 678"
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <button type="submit" className="btn btn-primary">
                  {editingOrg ? t('superAdmin.organizations.update') : t('superAdmin.organizations.create')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowModal(false);
                  setEditingOrg(null);
                  setFormData({ 
                    name: '', 
                    address: '',
                    billing_address_line1: '',
                    billing_address_line2: '',
                    billing_postal_code: '',
                    billing_city: '',
                    billing_country: 'Norge',
                    billing_email: '',
                    contact_name: '',
                    contact_email: '',
                    contact_phone: ''
                  });
                }}>{t('superAdmin.organizations.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminOrganizations;
